import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import PageLayout from '../components/ui/PageLayout';
import DataTable, { Column } from '../components/ui/DataTable';
import Modal from '../components/ui/Modal';
import Wizard, { WizardStep } from '../components/ui/Wizard';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';

const AR_API = `${API_V1}/ar-invoices`;
const REV_API = `${API_V1}/revenue`;

interface Contract { id: number; contract_number: string; customer_name: string; total_value: number; }
interface Modification { id: number; modification_date: string; modification_type: string; description: string; price_adjustment: number; cumulative_catchup: number; new_total_value: number | null; status: string; }
interface SSPItem { obligation_id: number; description: string; recognition_method: string; standalone_selling_price: number; allocated_price: number; amount_recognized: number; ssp_drift_pct: number; ssp_drift_alert: boolean; }

const MOD_COLORS: Record<string, string> = {
  SEPARATE_CONTRACT: 'var(--accent)',
  REALLOCATION: 'var(--warning)',
  CUMULATIVE_CATCHUP: 'var(--positive)',
};

// ASC 606-10-25-10 to 25-13 classifier:
//   distinct + SSP → SEPARATE_CONTRACT (10-25-12)
//   distinct + not-SSP → REALLOCATION (10-25-13(a) — termination + new)
//   not-distinct → CUMULATIVE_CATCHUP (10-25-13(b))
function classifyModification(distinct: boolean | null, sspReflecting: boolean | null): string | null {
  if (distinct === null) return null;
  if (distinct) {
    if (sspReflecting === null) return null;
    return sspReflecting ? 'SEPARATE_CONTRACT' : 'REALLOCATION';
  }
  return 'CUMULATIVE_CATCHUP';
}

function modTypeRationale(type: string): string {
  switch (type) {
    case 'SEPARATE_CONTRACT':
      return 'ASC 606-10-25-12: additional distinct goods/services AT their SSP — treated as an entirely new contract. No impact on existing recognition.';
    case 'REALLOCATION':
      return 'ASC 606-10-25-13(a): remaining goods/services are distinct but pricing deviates from SSP — terminate the existing contract and create a new one; reallocate the remaining transaction price across undelivered POBs.';
    case 'CUMULATIVE_CATCHUP':
      return 'ASC 606-10-25-13(b): remaining goods/services are NOT distinct from those transferred — part of a single partially-satisfied POB. Recognize the cumulative catch-up adjustment in the current period.';
    default:
      return '';
  }
}

export default function RevenueModifications() {
  const { entity } = useGlobalEntity();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [mods, setMods] = useState<Modification[]>([]);
  const [ssp, setSSP] = useState<SSPItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modDate, setModDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [modDescription, setModDescription] = useState('');
  const [priceAdjustment, setPriceAdjustment] = useState('');
  const [newTotalValue, setNewTotalValue] = useState('');
  const [cumulativeCatchup, setCumulativeCatchup] = useState('');
  const [isDistinct, setIsDistinct] = useState<boolean | null>(null);
  const [isSspReflecting, setIsSspReflecting] = useState<boolean | null>(null);
  const [manualOverride, setManualOverride] = useState<string | null>(null);
  const [overrideReason, setOverrideReason] = useState('');

  const classified = classifyModification(isDistinct, isSspReflecting);
  const finalType = manualOverride || classified;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(withEntityParam(`${AR_API}/contracts?limit=50`, entity));
      if (res.ok) { const d = await res.json(); setContracts(d.items || []); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [entity]);

  useEffect(() => { load(); }, [load]);

  const selectContract = async (id: number) => {
    setSelectedId(id);
    try {
      const [modRes, sspRes] = await Promise.all([
        authFetch(`${REV_API}/modifications/${id}`),
        authFetch(`${REV_API}/ssp/${id}`),
      ]);
      if (modRes.ok) { const d = await modRes.json(); setMods(d.modifications || []); }
      if (sspRes.ok) { const d = await sspRes.json(); setSSP(d.obligations || []); }
    } catch (e) { console.error(e); }
  };

  const openWizard = () => {
    setModDate(new Date().toISOString().slice(0, 10));
    setModDescription('');
    setPriceAdjustment('');
    setNewTotalValue('');
    setCumulativeCatchup('');
    setIsDistinct(null);
    setIsSspReflecting(null);
    setManualOverride(null);
    setOverrideReason('');
    setWizardOpen(true);
  };

  const submitModification = async () => {
    if (!selectedId || !finalType) return;
    setSubmitting(true);
    try {
      const body: Record<string, any> = {
        modification_type: finalType,
        modification_date: modDate,
        description: modDescription + (manualOverride && overrideReason ? `\n\n[Classifier override: ${overrideReason}]` : ''),
      };
      if (priceAdjustment) body.price_adjustment = parseFloat(priceAdjustment);
      if (newTotalValue) body.new_total_value = parseFloat(newTotalValue);
      if (cumulativeCatchup) body.cumulative_catchup = parseFloat(cumulativeCatchup);

      const res = await authFetch(`${REV_API}/modifications/${selectedId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const result = await res.json();
        showToast(result.treatment || 'Modification created', true);
        setWizardOpen(false);
        selectContract(selectedId);
      } else {
        const e = await res.json().catch(() => ({ detail: 'Failed' }));
        showToast(e.detail || 'Failed', false);
      }
    } catch (e: any) {
      showToast(e?.message || 'Network error', false);
    } finally {
      setSubmitting(false);
    }
  };

  const sspColumns: Column[] = [
    { key: 'description', label: 'Description' },
    { key: 'recognition_method', label: 'Method', render: (v: string) => <span style={{ fontSize: 11 }}>{v}</span> },
    { key: 'standalone_selling_price', label: 'SSP', align: 'right', render: (v: number) => formatAmount(v) },
    { key: 'allocated_price', label: 'Allocated', align: 'right', render: (v: number) => <span style={{ fontWeight: 600 }}>{formatAmount(v)}</span> },
    { key: 'amount_recognized', label: 'Recognized', align: 'right', render: (v: number) => <span style={{ color: 'var(--positive)' }}>{formatAmount(v)}</span> },
    { key: 'ssp_drift_pct', label: 'Drift', align: 'right', render: (v: number, row: SSPItem) => <span style={{ color: row.ssp_drift_alert ? 'var(--negative)' : 'var(--text-secondary)', fontWeight: row.ssp_drift_alert ? 700 : 400 }}>{v}%{row.ssp_drift_alert && ' !'}</span> },
  ];

  const selectedContract = contracts.find(c => c.id === selectedId);

  // Wizard steps
  const steps: WizardStep[] = [
    {
      key: 'describe',
      title: 'Describe the Modification',
      description: 'Record the effective date and any monetary adjustments.',
      isValid: () => !!modDate && !!modDescription.trim(),
      render: () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 600 }}>Effective date *</span>
            <input
              type="date"
              value={modDate}
              onChange={(e) => setModDate(e.target.value)}
              style={{ padding: 6, fontSize: 13, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 4, color: 'var(--text-primary)' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 600 }}>Description *</span>
            <textarea
              value={modDescription}
              onChange={(e) => setModDescription(e.target.value)}
              placeholder="Describe the change — e.g. 'Add 50 additional user seats at list price, effective 2026-05-01'"
              style={{ padding: 6, fontSize: 12, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 4, color: 'var(--text-primary)', resize: 'vertical', minHeight: 60, fontFamily: 'inherit' }}
            />
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600 }}>Price adjustment</span>
              <input
                type="number"
                step="0.01"
                value={priceAdjustment}
                onChange={(e) => setPriceAdjustment(e.target.value)}
                placeholder="0.00"
                style={{ padding: 6, fontSize: 13, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 4, color: 'var(--text-primary)' }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600 }}>New total value</span>
              <input
                type="number"
                step="0.01"
                value={newTotalValue}
                onChange={(e) => setNewTotalValue(e.target.value)}
                placeholder={selectedContract ? `${selectedContract.total_value}` : '0.00'}
                style={{ padding: 6, fontSize: 13, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 4, color: 'var(--text-primary)' }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600 }}>Cumulative catchup</span>
              <input
                type="number"
                step="0.01"
                value={cumulativeCatchup}
                onChange={(e) => setCumulativeCatchup(e.target.value)}
                placeholder="0.00"
                style={{ padding: 6, fontSize: 13, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 4, color: 'var(--text-primary)' }}
              />
            </label>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-hint)' }}>
            Optional. Provide only what applies to your modification. Classifier in step 2 will confirm the treatment.
          </div>
        </div>
      ),
    },
    {
      key: 'classify',
      title: 'Classify per ASC 606-10-25-10 to 25-13',
      description: 'Answer the two distinctness / pricing questions. The classifier computes the correct treatment — you can override with justification on the next step.',
      isValid: () => classified !== null,
      render: () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Q1: Distinct? */}
          <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 6, padding: 12, background: 'var(--bg-surface)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
              Q1. Are the modified goods/services <em>distinct</em> from those already transferred?
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 10 }}>
              A good/service is distinct if the customer can benefit from it on its own or with resources already available (ASC 606-10-25-19).
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[{ v: true, label: 'Yes — distinct' }, { v: false, label: 'No — part of existing POB' }].map(opt => (
                <label key={String(opt.v)} style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                  border: `1px solid ${isDistinct === opt.v ? 'var(--accent)' : 'var(--border-subtle)'}`,
                  background: isDistinct === opt.v ? 'rgba(59,130,246,0.08)' : 'var(--bg-card)',
                  borderRadius: 4, cursor: 'pointer', fontSize: 12,
                }}>
                  <input type="radio" checked={isDistinct === opt.v} onChange={() => setIsDistinct(opt.v)} />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          {/* Q2: SSP? — only relevant if Q1 = yes */}
          {isDistinct === true && (
            <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 6, padding: 12, background: 'var(--bg-surface)' }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                Q2. Does the price increase reflect the <em>standalone selling price</em> of the additional goods/services?
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 10 }}>
                Per ASC 606-10-25-12, both (1) distinct and (2) SSP-reflecting are required to treat as a separate contract.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[{ v: true, label: 'Yes — at SSP' }, { v: false, label: 'No — discounted or off-SSP' }].map(opt => (
                  <label key={String(opt.v)} style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                    border: `1px solid ${isSspReflecting === opt.v ? 'var(--accent)' : 'var(--border-subtle)'}`,
                    background: isSspReflecting === opt.v ? 'rgba(59,130,246,0.08)' : 'var(--bg-card)',
                    borderRadius: 4, cursor: 'pointer', fontSize: 12,
                  }}>
                    <input type="radio" checked={isSspReflecting === opt.v} onChange={() => setIsSspReflecting(opt.v)} />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Classification result */}
          {classified && (
            <div style={{
              padding: 12, borderRadius: 6,
              background: 'rgba(34,197,94,0.08)',
              border: '1px solid var(--positive)',
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>CLASSIFIED AS</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: MOD_COLORS[classified] || 'var(--text-primary)', marginBottom: 6 }}>
                {classified.replace(/_/g, ' ')}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {modTypeRationale(classified)}
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'review',
      title: 'Review & Confirm',
      description: 'Confirm the classification or override with a justification, then submit.',
      isValid: () => !!finalType && (!manualOverride || !!overrideReason.trim()),
      render: () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ padding: 12, background: 'var(--bg-surface)', borderRadius: 6, border: '1px solid var(--border-subtle)' }}>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Contract</div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{selectedContract?.contract_number}</div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
              {selectedContract?.customer_name} — {formatAmount(selectedContract?.total_value || 0)}
            </div>
          </div>

          <div style={{ padding: 12, borderRadius: 6, background: 'rgba(59,130,246,0.06)', border: '1px solid var(--accent)' }}>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>
              FINAL TREATMENT{manualOverride ? ' (OVERRIDE)' : ''}
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: MOD_COLORS[finalType || ''] || 'var(--text-primary)' }}>
              {finalType?.replace(/_/g, ' ')}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
              {modTypeRationale(finalType || '')}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Override classifier (optional)</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {['SEPARATE_CONTRACT', 'REALLOCATION', 'CUMULATIVE_CATCHUP'].map(t => {
                const isActive = manualOverride === t;
                const isClassifier = classified === t && !manualOverride;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setManualOverride(isActive ? null : t)}
                    style={{
                      padding: '4px 10px', fontSize: 11, fontWeight: 600,
                      background: isActive ? MOD_COLORS[t] : 'transparent',
                      color: isActive ? 'white' : (isClassifier ? 'var(--text-hint)' : 'var(--text-primary)'),
                      border: `1px solid ${isActive ? MOD_COLORS[t] : 'var(--border-subtle)'}`,
                      borderRadius: 4, cursor: 'pointer',
                    }}
                  >
                    {t.replace(/_/g, ' ')}{isClassifier ? ' (auto)' : ''}
                  </button>
                );
              })}
            </div>
            {manualOverride && (
              <textarea
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="Override justification (required) — explain why the classifier result is incorrect"
                style={{
                  width: '100%', padding: 6, marginTop: 8, fontSize: 12,
                  background: 'var(--bg-surface)',
                  border: `1px solid ${overrideReason.trim() ? 'var(--border-subtle)' : 'var(--negative)'}`,
                  borderRadius: 4, color: 'var(--text-primary)', resize: 'vertical', minHeight: 50, fontFamily: 'inherit',
                }}
              />
            )}
          </div>

          <div style={{ fontSize: 11, color: 'var(--text-hint)' }}>
            Submitting will call <code>/api/v1/revenue/modifications/{selectedId}</code>. If the selected type is REALLOCATION,
            the remaining transaction price will be redistributed across undelivered POBs proportional to their SSP.
          </div>
        </div>
      ),
    },
  ];

  return (
    <PageLayout title="Revenue Modifications" subtitle="ASC 606 contract modifications, SSP analysis, and catch-up adjustments">
      <div style={{ display: 'flex', gap: 16 }}>
        {/* Sidebar */}
        <div style={{ width: 280, flexShrink: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>Select Contract</div>
          {loading ? (
            <div style={{ color: 'var(--text-hint)', fontSize: 13 }}>Loading...</div>
          ) : contracts.map(c => (
            <div key={c.id} onClick={() => selectContract(c.id)} style={{ padding: '10px 12px', marginBottom: 4, borderRadius: 6, background: selectedId === c.id ? '#1e3a5f' : 'var(--bg-card)', color: selectedId === c.id ? '#fff' : 'var(--text-primary)', cursor: 'pointer', fontSize: 13, border: '1px solid var(--border-subtle)', transition: 'all 0.15s' }}>
              <div style={{ fontWeight: 600 }}>{c.contract_number}</div>
              <div style={{ fontSize: 11, opacity: 0.8 }}>{c.customer_name} — {formatAmount(c.total_value)}</div>
            </div>
          ))}
        </div>

        {/* Main content */}
        <div style={{ flex: 1 }}>
          {!selectedId ? (
            <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 60, textAlign: 'center', color: 'var(--text-hint)', fontSize: 13 }}>Select a contract to view modifications and SSP analysis.</div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16, justifyContent: 'space-between' }}>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  {selectedContract ? `${selectedContract.contract_number} — ${formatAmount(selectedContract.total_value)}` : ''}
                </div>
                <button
                  onClick={openWizard}
                  style={{ padding: '8px 14px', fontSize: 13, fontWeight: 600, background: 'var(--accent)', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                >
                  + New Modification
                </button>
              </div>

              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>SSP Analysis</div>
              <div style={{ marginBottom: 20 }}>
                <DataTable columns={sspColumns} rows={ssp} rowKey="obligation_id" emptyMessage="No obligations." />
              </div>

              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Modifications ({mods.length})</div>
              {mods.length === 0 ? (
                <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 20, textAlign: 'center', color: 'var(--text-hint)', fontSize: 13 }}>No modifications recorded.</div>
              ) : mods.map(m => (
                <div key={m.id} style={{ padding: 12, background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: 600, color: MOD_COLORS[m.modification_type] || 'var(--text-primary)', fontSize: 13 }}>{m.modification_type}</span>
                    <StatusBadge status={m.status} />
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{m.description || '-'}</div>
                  <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12 }}>
                    {m.price_adjustment !== 0 && <span>Adjustment: {formatAmount(m.price_adjustment)}</span>}
                    {m.cumulative_catchup !== 0 && <span style={{ color: 'var(--positive)' }}>Catch-up: {formatAmount(m.cumulative_catchup)}</span>}
                    {m.new_total_value != null && <span>New total: {formatAmount(m.new_total_value)}</span>}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Wizard modal */}
      <Modal
        open={wizardOpen}
        onClose={() => !submitting && setWizardOpen(false)}
        title="New Contract Modification"
        description={selectedContract ? `${selectedContract.contract_number} · ${selectedContract.customer_name}` : undefined}
        size="lg"
      >
        <Wizard
          steps={steps}
          onComplete={submitModification}
          onCancel={() => !submitting && setWizardOpen(false)}
          completeLabel="Create Modification"
          submitting={submitting}
        />
      </Modal>

      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)', color: toast.ok ? 'var(--positive)' : 'var(--negative)', fontSize: 13, fontWeight: 600 }}>{toast.msg}</div>}
    </PageLayout>
  );
}
